import { FormEvent, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ActionButton } from '@/components/ui/ActionButton';
import { apiClient } from '@/lib/api';
import type { ApprovalAction } from '@/lib/types';
import { formatDate } from './format';

const SLUG = 'ap-purchase-invoice';

interface APActivityCommentsProps {
  requestId: string;
  actions?: ApprovalAction[];
  onAction?: () => void;
}

function actionLabel(action: string, payload: Record<string, unknown> = {}): string {
  const n8nStatus = typeof payload.n8n_status === 'string' ? payload.n8n_status : null;
  if (
    action === 'approve' &&
    (['error', 'failed', 'timeout', 'transport_error'].includes(n8nStatus ?? '') ||
      payload.n8n_error ||
      payload.error_message)
  ) {
    return 'Approve failed';
  }

  const labels: Record<string, string> = {
    intake: 'Intake',
    'intake-reopened': 'Intake reopened',
    reprocess: 'Reprocessed',
    approve: 'Approved and posted',
    reject: 'Rejected',
    'service-posted': 'Service invoice posted',
    'post-service': 'Service invoice posted',
    'post-service-final': 'Service invoice posted',
    rollback: 'Rolled back to intake',
    'intake-rollback': 'Rolled back to intake',
    comment: 'Comment',
  };

  return (
    labels[action] ??
    action
      .split('-')
      .filter(Boolean)
      .map((part, index) =>
        index === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part,
      )
      .join(' ')
  );
}

function noteFromPayload(payload: Record<string, unknown>): string | null {
  const note = payload.note;
  return typeof note === 'string' && note.trim() ? note : null;
}

function apiErrorText(err: unknown): string {
  const raw = (err as { response?: { data?: { detail?: unknown } }; message?: string })
    ?.response?.data?.detail;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object') return JSON.stringify(raw);
  return (err as { message?: string })?.message ?? 'Could not add comment';
}

export function APActivityComments({
  requestId,
  actions = [],
  onAction,
}: APActivityCommentsProps) {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const trimmedNote = note.trim();

  const commentMutation = useMutation({
    mutationFn: async (body: { note: string }) => {
      const res = await apiClient.post(`/automations/${SLUG}/${requestId}/comment`, body);
      return res.data;
    },
    onSuccess: () => {
      setNote('');
      setError(null);
      onAction?.();
    },
    onError: (err: unknown) => {
      setError(apiErrorText(err));
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!trimmedNote || commentMutation.isPending) return;
    commentMutation.mutate({ note: trimmedNote });
  }

  return (
    <section className="rounded-lg border border-neutral-200 bg-neutral-0 p-16">
      <div className="mb-12 flex items-center justify-between gap-12">
        <h3 className="text-sm font-semibold text-neutral-900">Activity</h3>
        <span className="text-xs text-neutral-500">{actions.length} actions</span>
      </div>

      {actions.length > 0 ? (
        <ol className="space-y-12">
          {actions.map((item) => {
            const noteText = noteFromPayload(item.payload);

            return (
              <li
                key={item.id}
                className="border-l-2 border-neutral-200 pl-12 text-sm text-neutral-700"
              >
                <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
                  <span className="font-medium text-neutral-900">
                    {actionLabel(item.action, item.payload)}
                  </span>
                  <span className="text-xs text-neutral-500">
                    {item.actor_email} at {formatDate(item.created_at)}
                  </span>
                </div>
                {noteText && (
                  <p className="mt-4 whitespace-pre-wrap text-neutral-800">{noteText}</p>
                )}
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="text-sm text-neutral-500">No activity recorded yet.</p>
      )}

      <form onSubmit={handleSubmit} className="mt-16 border-t border-neutral-200 pt-12">
        <label className="block text-xs font-medium text-neutral-700" htmlFor="ap-comment">
          Add comment
        </label>
        <textarea
          id="ap-comment"
          className="mt-6 block min-h-[96px] w-full resize-y rounded-md border border-neutral-200 bg-neutral-0 px-12 py-8 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          value={note}
          onChange={(event) => {
            setNote(event.target.value);
            if (error) setError(null);
          }}
          placeholder="Add an audit note for this request."
        />
        {error && <p className="mt-8 text-sm text-error-700">{error}</p>}
        <div className="mt-12 flex justify-end">
          <ActionButton
            type="submit"
            size="sm"
            disabled={!trimmedNote || commentMutation.isPending}
            loading={commentMutation.isPending}
          >
            Add comment
          </ActionButton>
        </div>
      </form>
    </section>
  );
}
